import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Scale,
  ChevronLeft,
  Palette,
  FileText,
  List,
  Shield,
  Mail,
  Save,
  Loader2,
  Upload,
  Trash2,
  Plus,
  GripVertical,
  X,
  Eye,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default form sections
const DEFAULT_SECTIONS = {
  contact_info: { label: "Contact Information", description: "Name, email, phone, address" },
  case_details: { label: "Case Details", description: "Case type, description, lead source" },
  related_parties: { label: "Related Parties", description: "Opposing parties, family members" },
  attorneys_dates: { label: "Attorneys & Dates", description: "Prior attorneys, court dates" },
  documents: { label: "Documents", description: "File uploads" },
  scheduling: { label: "Scheduling", description: "Book a consultation" }
};

export default function IntakeFormCustomizationPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [settings, setSettings] = useState({});
  const [newCaseType, setNewCaseType] = useState('');
  const [newQuestion, setNewQuestion] = useState({ question: '', type: 'text', required: false, options: '' });

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/intake/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/intake/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      if (res.ok) {
        toast.success('Form customization saved!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_URL}/api/intake/settings/upload-logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        setSettings({ ...settings, firm_logo_url: data.logo_url });
        toast.success('Logo uploaded successfully!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to upload logo');
      }
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const deleteLogo = async () => {
    try {
      const res = await fetch(`${API_URL}/api/intake/settings/logo`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setSettings({ ...settings, firm_logo_url: null });
        toast.success('Logo deleted');
      }
    } catch (error) {
      toast.error('Failed to delete logo');
    }
  };

  const addCaseType = () => {
    if (!newCaseType.trim()) return;
    const currentTypes = settings.case_types || [];
    if (currentTypes.includes(newCaseType.trim())) {
      toast.error('Case type already exists');
      return;
    }
    setSettings({ ...settings, case_types: [...currentTypes, newCaseType.trim()] });
    setNewCaseType('');
  };

  const removeCaseType = (type) => {
    const currentTypes = settings.case_types || [];
    setSettings({ ...settings, case_types: currentTypes.filter(t => t !== type) });
  };

  const addCustomQuestion = () => {
    if (!newQuestion.question.trim()) return;
    const currentQuestions = settings.custom_questions || [];
    const question = {
      id: `q_${Date.now()}`,
      question: newQuestion.question.trim(),
      type: newQuestion.type,
      required: newQuestion.required,
      options: newQuestion.type === 'select' ? newQuestion.options.split(',').map(o => o.trim()).filter(o => o) : []
    };
    setSettings({ ...settings, custom_questions: [...currentQuestions, question] });
    setNewQuestion({ question: '', type: 'text', required: false, options: '' });
  };

  const removeCustomQuestion = (id) => {
    const currentQuestions = settings.custom_questions || [];
    setSettings({ ...settings, custom_questions: currentQuestions.filter(q => q.id !== id) });
  };

  const updateSection = (sectionKey, field, value) => {
    const currentSections = settings.form_sections || {};
    setSettings({
      ...settings,
      form_sections: {
        ...currentSections,
        [sectionKey]: {
          ...currentSections[sectionKey],
          [field]: value
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-white">
        <div className="container flex items-center justify-between h-16 px-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/intake/settings')}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Settings
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-emerald-600" />
              <span className="font-semibold">Form Customization</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {settings.intake_slug && settings.public_intake_enabled ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`/intake/${settings.intake_slug}`, '_blank')}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview Live Form
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                disabled
                title="Enable public intake link in Settings first"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview (Enable Public Link First)
              </Button>
            )}
            <Button onClick={saveSettings} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save All Changes
            </Button>
          </div>
        </div>
      </header>

      {/* Preview Banner */}
      {settings.intake_slug && settings.public_intake_enabled && (
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-3">
          <div className="container max-w-5xl mx-auto px-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5" />
              <span className="font-medium">Preview your customized form:</span>
              <code className="bg-white/20 px-2 py-1 rounded text-sm">
                /intake/{settings.intake_slug}
              </code>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => window.open(`/intake/${settings.intake_slug}`, '_blank')}
              className="bg-white text-emerald-700 hover:bg-emerald-50"
            >
              Open Preview <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      )}

      <main className="container max-w-5xl mx-auto py-8 px-4">
        <Tabs defaultValue="branding" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="branding" className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Branding
            </TabsTrigger>
            <TabsTrigger value="sections" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Sections
            </TabsTrigger>
            <TabsTrigger value="case-types" className="flex items-center gap-2">
              <List className="w-4 h-4" />
              Case Types
            </TabsTrigger>
            <TabsTrigger value="legal" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Legal
            </TabsTrigger>
            <TabsTrigger value="auto-response" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Auto-Response
            </TabsTrigger>
          </TabsList>

          {/* Branding Tab */}
          <TabsContent value="branding">
            <Card>
              <CardHeader>
                <CardTitle>Branding & Appearance</CardTitle>
                <CardDescription>Customize the look and feel of your intake form</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Logo Upload */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Firm Logo</Label>
                  <p className="text-sm text-muted-foreground">
                    Upload your firm's logo to display on the intake form (max 2MB)
                  </p>
                  <div className="flex items-center gap-4">
                    {settings.firm_logo_url ? (
                      <div className="relative">
                        <img
                          src={`${API_URL}${settings.firm_logo_url}`}
                          alt="Firm Logo"
                          className="h-16 w-auto border rounded-lg p-2"
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute -top-2 -right-2 h-6 w-6"
                          onClick={deleteLogo}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="h-16 w-32 border-2 border-dashed rounded-lg flex items-center justify-center text-muted-foreground">
                        No logo
                      </div>
                    )}
                    <div>
                      <input
                        type="file"
                        id="logo-upload"
                        accept="image/*"
                        className="hidden"
                        onChange={handleLogoUpload}
                      />
                      <Button
                        variant="outline"
                        onClick={() => document.getElementById('logo-upload').click()}
                        disabled={uploadingLogo}
                      >
                        {uploadingLogo ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {settings.firm_logo_url ? 'Change Logo' : 'Upload Logo'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Welcome Message */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Welcome Message</Label>
                  <p className="text-sm text-muted-foreground">
                    Custom introduction text displayed at the top of the form
                  </p>
                  <Textarea
                    placeholder="Welcome! Please complete this form to help us understand your legal needs..."
                    value={settings.welcome_message || ''}
                    onChange={(e) => setSettings({ ...settings, welcome_message: e.target.value })}
                    rows={3}
                  />
                </div>

                {/* Primary Color */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Primary Color</Label>
                  <p className="text-sm text-muted-foreground">
                    Choose a color that matches your firm's branding
                  </p>
                  <div className="flex items-center gap-4">
                    <input
                      type="color"
                      value={settings.primary_color || '#1e3a5f'}
                      onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                      className="w-12 h-12 rounded-lg cursor-pointer border"
                    />
                    <Input
                      value={settings.primary_color || '#1e3a5f'}
                      onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                      className="w-32 font-mono"
                      placeholder="#1e3a5f"
                    />
                    <div
                      className="h-12 flex-1 rounded-lg flex items-center justify-center text-white font-medium"
                      style={{ backgroundColor: settings.primary_color || '#1e3a5f' }}
                    >
                      Preview
                    </div>
                  </div>
                </div>

                {/* Live Preview Card */}
                <div className="pt-6 border-t">
                  <h4 className="font-medium mb-3">Form Header Preview</h4>
                  <div className="border rounded-lg p-4 bg-slate-50">
                    <div className="flex items-center gap-3">
                      {settings.firm_logo_url ? (
                        <img
                          src={`${API_URL}${settings.firm_logo_url}`}
                          alt="Logo"
                          className="h-10 w-auto"
                        />
                      ) : (
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: settings.primary_color || '#1e3a5f' }}
                        >
                          <Scale className="w-6 h-6 text-white" />
                        </div>
                      )}
                      <h2 
                        className="text-xl font-bold"
                        style={{ color: settings.primary_color || '#1e3a5f' }}
                      >
                        {settings.firm_display_name || 'Your Firm Name'}
                      </h2>
                    </div>
                    {settings.welcome_message && (
                      <p className="mt-3 text-sm text-muted-foreground">
                        {settings.welcome_message}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Form Sections Tab */}
          <TabsContent value="sections">
            <Card>
              <CardHeader>
                <CardTitle>Form Sections</CardTitle>
                <CardDescription>Enable or disable sections and mark them as required</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(DEFAULT_SECTIONS).map(([key, section]) => {
                  const sectionSettings = settings.form_sections?.[key] || { enabled: true, required: key === 'contact_info' || key === 'case_details' };
                  return (
                    <div key={key} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <GripVertical className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{section.label}</p>
                          <p className="text-sm text-muted-foreground">{section.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <Label className="text-sm text-muted-foreground">Required</Label>
                          <Switch
                            checked={sectionSettings.required}
                            onCheckedChange={(checked) => updateSection(key, 'required', checked)}
                            disabled={!sectionSettings.enabled}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-sm text-muted-foreground">Enabled</Label>
                          <Switch
                            checked={sectionSettings.enabled}
                            onCheckedChange={(checked) => updateSection(key, 'enabled', checked)}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Custom Questions */}
                <div className="pt-6 border-t mt-6">
                  <h3 className="font-semibold mb-4">Custom Questions</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Add your own questions to gather specific information
                  </p>
                  
                  {/* Existing Questions */}
                  {settings.custom_questions?.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {settings.custom_questions.map((q) => (
                        <div key={q.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{q.question}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline">{q.type}</Badge>
                              {q.required && <Badge variant="secondary">Required</Badge>}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeCustomQuestion(q.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add New Question */}
                  <div className="p-4 border rounded-lg space-y-3">
                    <Input
                      placeholder="Enter your question..."
                      value={newQuestion.question}
                      onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                    />
                    <div className="flex items-center gap-4">
                      <select
                        className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={newQuestion.type}
                        onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value })}
                      >
                        <option value="text">Text Input</option>
                        <option value="textarea">Long Text</option>
                        <option value="select">Dropdown</option>
                        <option value="checkbox">Checkbox</option>
                      </select>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={newQuestion.required}
                          onCheckedChange={(checked) => setNewQuestion({ ...newQuestion, required: checked })}
                        />
                        <Label className="text-sm">Required</Label>
                      </div>
                      <Button onClick={addCustomQuestion}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Question
                      </Button>
                    </div>
                    {newQuestion.type === 'select' && (
                      <Input
                        placeholder="Options (comma-separated): Option 1, Option 2, Option 3"
                        value={newQuestion.options}
                        onChange={(e) => setNewQuestion({ ...newQuestion, options: e.target.value })}
                      />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Case Types Tab */}
          <TabsContent value="case-types">
            <Card>
              <CardHeader>
                <CardTitle>Case Types</CardTitle>
                <CardDescription>Customize the list of case types clients can select</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Default Case Type */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Default Case Type</Label>
                  <p className="text-sm text-muted-foreground">
                    Pre-select a case type (optional)
                  </p>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={settings.default_case_type || ''}
                    onChange={(e) => setSettings({ ...settings, default_case_type: e.target.value || null })}
                  >
                    <option value="">No default</option>
                    {(settings.case_types || []).map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {/* Case Types List */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Available Case Types</Label>
                  <div className="flex flex-wrap gap-2">
                    {(settings.case_types || []).map((type) => (
                      <Badge key={type} variant="secondary" className="px-3 py-1.5 text-sm">
                        {type}
                        <button
                          className="ml-2 hover:text-red-500"
                          onClick={() => removeCaseType(type)}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Add New Case Type */}
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Enter new case type..."
                    value={newCaseType}
                    onChange={(e) => setNewCaseType(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addCaseType()}
                  />
                  <Button onClick={addCaseType}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Legal/Compliance Tab */}
          <TabsContent value="legal">
            <Card>
              <CardHeader>
                <CardTitle>Legal & Compliance</CardTitle>
                <CardDescription>Customize disclaimers and consent requirements</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Custom Disclaimer */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Custom Disclaimer</Label>
                  <p className="text-sm text-muted-foreground">
                    Add a disclaimer or important notice displayed on the form
                  </p>
                  <Textarea
                    placeholder="By submitting this form, you understand that the information provided does not create an attorney-client relationship..."
                    value={settings.custom_disclaimer || ''}
                    onChange={(e) => setSettings({ ...settings, custom_disclaimer: e.target.value })}
                    rows={4}
                  />
                </div>

                {/* Privacy Policy URL */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Privacy Policy URL</Label>
                  <p className="text-sm text-muted-foreground">
                    Link to your firm's privacy policy
                  </p>
                  <Input
                    type="url"
                    placeholder="https://yourfirm.com/privacy-policy"
                    value={settings.privacy_policy_url || ''}
                    onChange={(e) => setSettings({ ...settings, privacy_policy_url: e.target.value })}
                  />
                </div>

                {/* Consent Checkbox */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-medium">Require Consent Checkbox</Label>
                      <p className="text-sm text-muted-foreground">
                        Require clients to agree to terms before submitting
                      </p>
                    </div>
                    <Switch
                      checked={settings.require_consent ?? true}
                      onCheckedChange={(checked) => setSettings({ ...settings, require_consent: checked })}
                    />
                  </div>
                  {settings.require_consent !== false && (
                    <Textarea
                      placeholder="I consent to the collection and processing of my information..."
                      value={settings.consent_checkbox_text || ''}
                      onChange={(e) => setSettings({ ...settings, consent_checkbox_text: e.target.value })}
                      rows={2}
                    />
                  )}
                </div>

                {/* Confirmation Message */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Custom Confirmation Message</Label>
                  <p className="text-sm text-muted-foreground">
                    Message shown after successful form submission
                  </p>
                  <Textarea
                    placeholder="Thank you for your submission. Our team will review your information and contact you within 24-48 hours."
                    value={settings.confirmation_message || ''}
                    onChange={(e) => setSettings({ ...settings, confirmation_message: e.target.value })}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Auto-Response Tab */}
          <TabsContent value="auto-response">
            <Card>
              <CardHeader>
                <CardTitle>Auto-Response Email</CardTitle>
                <CardDescription>Automatically send a confirmation email to clients</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable Auto-Response */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Enable Auto-Response</Label>
                    <p className="text-sm text-muted-foreground">
                      Send an automatic email to clients after form submission
                    </p>
                  </div>
                  <Switch
                    checked={settings.auto_response_enabled || false}
                    onCheckedChange={(checked) => setSettings({ ...settings, auto_response_enabled: checked })}
                  />
                </div>

                <div className={settings.auto_response_enabled ? '' : 'opacity-50 pointer-events-none'}>
                  {/* Email Subject */}
                  <div className="space-y-3">
                    <Label className="text-base font-medium">Email Subject</Label>
                    <Input
                      placeholder="Thank you for your inquiry"
                      value={settings.auto_response_subject || ''}
                      onChange={(e) => setSettings({ ...settings, auto_response_subject: e.target.value })}
                    />
                  </div>

                  {/* Email Message */}
                  <div className="space-y-3 mt-4">
                    <Label className="text-base font-medium">Email Message</Label>
                    <p className="text-sm text-muted-foreground">
                      Customize the body of the auto-response email
                    </p>
                    <Textarea
                      placeholder={`Thank you for contacting us. We have received your intake form and will review your information shortly.

A member of our team will be in touch with you within 1-2 business days to discuss your case.

If you have any urgent matters, please don't hesitate to call our office directly.`}
                      value={settings.auto_response_message || ''}
                      onChange={(e) => setSettings({ ...settings, auto_response_message: e.target.value })}
                      rows={8}
                    />
                  </div>

                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mt-4">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> The auto-response email will automatically include:
                    </p>
                    <ul className="text-sm text-blue-700 mt-2 space-y-1">
                      <li>• Your firm logo (if uploaded)</li>
                      <li>• Firm name</li>
                      <li>• Client's name</li>
                      <li>• Reference number</li>
                      <li>• Case type</li>
                      <li>• Submission date</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
